import { useState } from 'react';
import { format } from 'date-fns';
import useApiFetchAll from '@/hooks/useApiFetchAll';
import staffMemberService from '@/services/staffMember';
import teamService from '@/services/teams';
import { Team } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import type { Props, RowData } from './types';
import ExportMembersModalComponent from './ExportMembersModal.component';
import { HEADERS } from './ExportMembersModal.constants';

const ExportMembersModal = (componentProps: Props): JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const [selectedFields, setSelectedFields] = useState<(keyof RowData)[]>([]);
  const [isFetchingAdditionalData, setIsFetchingAdditionalData] = useState<boolean>(false);
  const fetchStaffMembers = useApiFetchAll<StaffMember>(staffMemberService.listStaffMembers, {
    take: 30,
  });

  const handleSelectField = (field: keyof RowData): void => {
    setSelectedFields((current) => [...current, field]);
  };

  const handleRemoveField = (removedField: keyof RowData): void => {
    setSelectedFields((current) => current.filter((field) => field !== removedField));
  };

  const handleExportClick = async (): Promise<void> => {
    setIsFetchingAdditionalData(true);

    try {
      const staffMembers = await fetchStaffMembers();
      const csvRows: string[] = [];
      csvRows.push(selectedFields.map((field) => HEADERS[field]).join(','));
      const staffMemberRows = staffMembers.map((staffMember) => {
        const currentCompensation =
          staffMember.compensationHistory.length > 0
            ? staffMember.compensationHistory[staffMember.compensationHistory.length - 1]
            : undefined;

        const currentJob = staffMember.jobInfo.reduce<StaffMember['jobInfo'][0] | undefined>(
          (lastCurrentJob, jobInfo) => {
            if (
              !jobInfo.toDate &&
              (!lastCurrentJob || new Date(lastCurrentJob.fromDate) < new Date(jobInfo.fromDate))
            ) {
              return jobInfo;
            }
            return lastCurrentJob;
          },
          undefined
        );

        const staffMemberData: RowData = {
          addressLine1: staffMember.person.addressLine1 || '',
          addressLine2: staffMember.person.addressLine2 || '',
          birthDate: staffMember.person.birthdate
            ? format(new Date(staffMember.person.birthdate), 'MM/dd/yyyy')
            : '',
          city: staffMember.person.city || '',
          contractStatus: staffMember.contract?.status || '',
          contractType: staffMember.contract?.type || '',
          country: staffMember.person.country || '',
          currentCompensationChangeReason: currentCompensation?.changeReason || '',
          currentCompensationHourlyRate: currentCompensation?.hourlyRate || '',
          currentCompensationStartDate: currentCompensation?.startDate
            ? format(new Date(currentCompensation.startDate), 'MM/dd/yyyy')
            : '',
          currentJobArea: currentJob?.area || '',
          currentJobDirectLeader: currentJob?.directLeaderId || '',
          currentJobDirectReports: (currentJob?.directReports || [])
            .map(
              (reporter) =>
                `${reporter.staffMember.person.firstName} ${reporter.staffMember.person.lastName}`
            )
            .join('; '),
          currentJobPartner: '',
          currentJobSeniority: currentJob?.seniority || '',
          currentJobStartDate: currentJob?.fromDate
            ? format(new Date(currentJob.fromDate), 'MM/dd/yyyy')
            : '',
          currentJobTeam: currentJob?.teamId || '',
          currentJobTitle: currentJob?.title || '',
          currentEmergencyContactEmail: staffMember.person.emergencyContactEmail || '',
          currentEmergencyContactName: staffMember.person.emergencyContactName || '',
          emergencyContactPhone: staffMember.person.emergencyContactPhone || '',
          emergencyContactRelationship: staffMember.person.emergencyContactRelationship || '',
          ethnicity: staffMember.person.ethnicity || '',
          firstName: staffMember.person.firstName || '',
          gender: staffMember.person.gender || '',
          githubLink: staffMember.person.githubUrl || '',
          hireDate: staffMember.hiringDate
            ? format(new Date(staffMember.hiringDate), 'MM/dd/yyyy')
            : '',
          lastName: staffMember.person.lastName || '',
          linkedinUrl: staffMember.person.linkedinUrl || '',
          personalEmail: staffMember.person.email || '',
          phone: staffMember.person.mobilePhone || '',
          state: staffMember.person.state || '',
          status: staffMember.person.status || '',
          trioProfileLink: staffMember.person.trioProfileUrl || '',
          workEmail: staffMember.person.workEmail || '',
          zipCode: staffMember.person.zipCode || '',
        };
        return staffMemberData;
      });

      const teamsToFetch: Record<string, string> = {};
      staffMemberRows.forEach((staffMemberData) => {
        if (staffMemberData.currentJobTeam) {
          teamsToFetch[staffMemberData.currentJobTeam] = staffMemberData.currentJobTeam;
        }
      });

      const teamsResults = await Promise.allSettled(
        Object.keys(teamsToFetch).map((teamId) => teamService.getTeam(teamId))
      );

      const teamsData = teamsResults.reduce<Record<string, Team>>((obj, curr) => {
        if (curr.status === 'fulfilled') {
          return {
            ...obj,
            [curr.value.data.id]: curr.value.data,
          };
        }
        return obj;
      }, {});

      const staffMembersToFetch: Record<string, string> = {};
      staffMemberRows.forEach((staffMemberData) => {
        if (staffMemberData.currentJobDirectLeader) {
          staffMembersToFetch[staffMemberData.currentJobDirectLeader] =
            staffMemberData.currentJobDirectLeader;
        }
      });

      const staffMembersResults = await Promise.allSettled(
        Object.keys(staffMembersToFetch).map((staffMemberId) =>
          staffMemberService.getStaffMember({ staffMemberId })
        )
      );

      const staffMembersData = staffMembersResults.reduce<Record<string, StaffMember>>(
        (obj, curr) => {
          if (curr.status === 'fulfilled') {
            return {
              ...obj,
              [curr.value.data.id]: curr.value.data,
            };
          }
          return obj;
        },
        {}
      );

      staffMemberRows.forEach((staffMemberData) => {
        if (staffMemberData.currentJobTeam) {
          // eslint-disable-next-line no-param-reassign
          staffMemberData.currentJobTeam =
            teamsData[staffMemberData.currentJobTeam].name || staffMemberData.currentJobTeam;
        }
        if (staffMemberData.currentJobDirectLeader) {
          // eslint-disable-next-line no-param-reassign
          staffMemberData.currentJobDirectLeader = staffMembersData[
            staffMemberData.currentJobDirectLeader
          ]
            ? `${staffMembersData[staffMemberData.currentJobDirectLeader].person.firstName} ${
                staffMembersData[staffMemberData.currentJobDirectLeader].person.lastName
              }`
            : staffMemberData.currentJobDirectLeader;
        }
      });

      staffMemberRows.forEach((staffMemberData) => {
        csvRows.push(selectedFields.map((field) => staffMemberData[field]).join(','));
      });

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', 'download.csv');
      a.click();
      a.parentElement?.removeChild(a);
    } finally {
      setIsFetchingAdditionalData(false);
    }
  };

  return (
    <ExportMembersModalComponent
      search={search}
      onSearchChange={setSearch}
      isLoadingAdditionalData={isFetchingAdditionalData}
      selectedFields={selectedFields}
      onSelectField={handleSelectField}
      onRemoveField={handleRemoveField}
      onExportClick={handleExportClick}
      {...componentProps}
    />
  );
};

export default ExportMembersModal;
